import { commissionDiscounts } from '../consts/constants';
import { useUserStore } from '@/stores/user';
import {
  calculateWin,
  convertAmericanToPercent,
} from './utils';
/*
4caster commission rules

The commission percent standard is 0.01, can be decreased either by discounts based on
league and bet type like mlb moneylines being .0075 commission
Or by certain users having negociated lower commission

The commission amount on a lost bet is the less of the win / risk, so on a favorite
the commission is taken from the amount the user could have won, while on a dog the
commission will be taken from the amount risked on the bet.

If a user wins the bet the commission will be taken from the winning amount
*/

// the core function to get commission percent factoring in the users default commission
// percent from the store as well as commission discounts
export function getCommissionPercent(league, betType) {
  const userStore = useUserStore();
  const userPct = userStore.user.commissionCharged || 0.01;
  if (commissionDiscounts[league]?.[betType]) {
    return Math.min(commissionDiscounts[league][betType], userPct);
  }
  return userPct;
}

// this is basically just used in displaying what others will see in Bid ask ladder
// while a user is in the process of creating an offer
export function discountOnlyCommissionPercent(league, betType) {
  if (commissionDiscounts[league]?.[betType]) {
    return commissionDiscounts[league][betType];
  } else {
    return 0.01;
  }
}

// function to get dispaly volume from sum untaken, this basically factors in
// user view commission settings to bet volume
// min val untaken is the min between the bet and the sumUntaken, aka the less of the risk win
export function getDisplayVolume(sumUntaken, minValUntaken, league, betType) {
  const userStore = useUserStore();
  if (userStore.user.viewOddsWithCommission) {
    const commissionPct = getCommissionPercent(league, betType);
    return sumUntaken + minValUntaken * commissionPct;
  }
  return sumUntaken;
}

// function to format odds display based on user commission preference
// `displayDecimal` accepts:
//   true       → round to one decimal (for display, e.g. "-115.3")
//   false      → round to whole integer (for display, e.g. "-115")
//   'precise'  → no rounding; full precision for internal math (risk/win sizing)
export function oddsWithCommission(
  odds,
  league,
  betType,
  displayDecimal,
  userStore = useUserStore(),
) {
  if (userStore.user.viewOddsWithCommission) {
    let commissionPct = userStore.user.commissionCharged
      ? userStore.user.commissionCharged
      : 0.01;

    // if there is a current discount for commission of this combo, ie, 0.005% on MLB Moneylines
    if (league && betType && commissionDiscounts[league]?.[betType]) {
      commissionPct = Math.min(
        commissionPct,
        commissionDiscounts[league][betType],
      );
    }

    let commissionAdjustedOdds;
    let risk;
    let win;
    let adjRisk;
    if (odds > 0) {
      risk = 100;
      win = odds;
      adjRisk = risk + risk * commissionPct;
    } else {
      risk = Math.abs(odds);
      win = 100;
      adjRisk = risk + win * commissionPct;
    }
    const adjWin = win - win * commissionPct;
    if (adjWin >= adjRisk) {
      commissionAdjustedOdds = 100 * (adjWin / adjRisk);
    } else {
      commissionAdjustedOdds = -100 * (adjRisk / adjWin);
    }
    if (displayDecimal === 'precise') return commissionAdjustedOdds;
    return displayDecimal
      ? Math.round(commissionAdjustedOdds * 10) / 10
      : Math.round(commissionAdjustedOdds);
  }
  return odds;
}

// ---- PRO-234: commission-consistent slippage scale ----
// A commission-view user reads every price on the commission-adjusted scale,
// so their slippage tolerance is a promise on THAT scale. The book and the
// matching engine are raw. These convert between the two implied-probability
// scales (0..1). Derived from oddsWithCommission above:
//   raw favorite  (pRaw >= 0.5): pAdj = pRaw·(1−c) + c        (linear, shrinks deltas)
//   raw underdog  (pRaw <  0.5): pAdj = pRaw·(1+c) / (1 − c·(1 − 2·pRaw))
// Both branches are continuous at pRaw = 0.5.
export function impliedProbWithCommission(pRaw, commissionPct) {
  const c = commissionPct;
  if (pRaw >= 0.5) return pRaw * (1 - c) + c;
  return (pRaw * (1 + c)) / (1 - c * (1 - 2 * pRaw));
}

export function impliedProbWithoutCommission(pAdj, commissionPct) {
  const c = commissionPct;
  const negBranch = (pAdj - c) / (1 - c);
  if (negBranch >= 0.5) return negBranch;
  return (pAdj * (1 - c)) / (1 + c - 2 * c * pAdj);
}

// Converts a tolerance the user set on their displayed (commission-adjusted)
// odds scale into raw implied-prob points for the engine, exact for the price
// being taken. Shrink-only: where the raw equivalent would exceed the
// configured value (favorites) it is capped at the configured value, so the
// realized slip on the user's displayed scale can never exceed the setting in
// either direction — and any error here fails toward LESS slippage, never
// more. Identity for non-commission users and non-positive tolerances.
export function rawSlippageForDisplayedTolerance(
  rawOdds,
  displayedPct,
  league,
  betType,
  userStore = useUserStore(),
) {
  if (!userStore.user.viewOddsWithCommission || !(displayedPct > 0)) {
    return displayedPct;
  }
  const c = getCommissionPercent(league, betType);
  const pRaw = convertAmericanToPercent(rawOdds);
  if (!Number.isFinite(pRaw) || pRaw === null) return displayedPct;
  const pAdj = impliedProbWithCommission(pRaw, c);
  const pAdjFloor = Math.min(pAdj + displayedPct / 100, 0.99);
  const pRawFloor = impliedProbWithoutCommission(pAdjFloor, c);
  const rawPct = (pRawFloor - pRaw) * 100;
  if (!Number.isFinite(rawPct) || rawPct <= 0) return 0;
  return Math.min(rawPct, displayedPct);
}

// gets a string risk from take bet modal, returns a number
export function getRiskAmount(risk, win, worstOdds, league, betType) {
  const userStore = useUserStore();
  let riskAmt = parseFloat(risk);
  if (userStore.user.viewOddsWithCommission) {
    const commissionPct = getCommissionPercent(league, betType);
    if (worstOdds > 0) {
      riskAmt /= 1 + commissionPct;
    } else {
      riskAmt -= win * commissionPct;
    }
  }
  return riskAmt;
}

export function commissionAdjustedRisk(
  risk,
  odds,
  league,
  betType,
  userStore = useUserStore(),
) {
  if (!userStore.user.viewOddsWithCommission) {
    return risk;
  }
  const commissionPct = getCommissionPercent(league, betType);
  if (odds > 0) return risk * (1 + commissionPct);
  const win = (100 / Math.abs(odds)) * risk;
  const newRisk = risk + win * commissionPct;
  return newRisk;
}

export function commissionAdjustedWin(
  win,
  league,
  betType,
  userStore = useUserStore(),
) {
  if (!userStore.user.viewOddsWithCommission) {
    return win;
  }
  const nWin = parseFloat(win);
  const commissionPct = getCommissionPercent(league, betType);
  const newWin = nWin - nWin * commissionPct;
  return newWin;
}

// this function is used in take bet modal, it used to check if the users
// bet plus commission is over available balance
export function getBetPlusCommission(risk, odds, league, betType) {
  const userStore = useUserStore();
  const nRisk = parseFloat(risk);
  if (!userStore.user.viewOddsWithCommission) {
    const commissionPct = getCommissionPercent(league, betType);
    if (odds < 1) {
      const win = calculateWin(risk, odds);
      return nRisk + win * commissionPct;
    } else {
      return nRisk + nRisk * commissionPct;
    }
  } else {
    // if the user has view odds with commission on we can just return risk
    // because the commission will be factored in to the risk amount
    return nRisk;
  }
}

// this function is also called in take bet modal if the user does not have view odds with commission
// and inputs a risk over their available balance, it should return the the max available risk
// input allowed
export function getMaxRiskFromAvailable(available, odds, league, betType) {
  return 'in progress';
}

// this one calculates the displayed win amount for a user offer when the user is creating and offer
// in the take bet modal. it will properly show the volume that others will see if the user has
// view odds without commission on
export function calculateWinForNoVig(risk, odds, league, betType) {
  const userStore = useUserStore();
  const regWin = calculateWin(risk, odds);
  if (!userStore.user.viewOddsWithCommission) {
    return regWin;
  }
  const commissionPct = discountOnlyCommissionPercent(league, betType);
  const commissionCharged = regWin * commissionPct;
  return regWin + commissionCharged;
}

export function calculateTotalSumUntakenForNoVig(sessions, league, betType) {
  // const commissionPct = getCommissionPercent(league, betType);
  if (!sessions || !sessions[0] || !sessions[0].odds) {
    return 0;
  }
  return sessions.reduce(
    (totalValue, currentValue) => totalValue + currentValue.volume,
    0,
  );
}
